import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';
import {
  SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS,
  SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS,
  SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS,
} from '../scenario-investment-evidence-artifacts';

// origin/main literals: apps/api/src/decision-modules/finance/finance.types.ts
// (FINANCE_DATA_LAKE_CONTEXT_KIND, FINANCE_DATA_LAKE_SCHEMA_VERSION), which
// re-export packages/lib ASK_HELIX_FINANCE_DATA_LAKE_* — the exact pair
// FinanceGovernedDataLakeSource.loadLatestApprovedSnapshot() queries.
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): 10 APPROVED,
// ingest-anchored rows under this contextKind/schemaVersion pair, latest
// approvedAt 2026-07-13, top-level asOf 2025-12-31. The freshest artifact's own
// evidenceRefs carry exactly these three evidence classes, every one
// gapStatus=available, confidence=high — so all three entries below restate the
// artifact rather than asserting anything it does not contain.
//
// SCOPE NOTE (deliberate, disclosed — the org-analysis-optimization precedent),
// AMENDED by #4602 Q1-FIX PR-B (2026-08-18): finance's manifest declares eight
// distinct requiredEvidenceKeys across its 18 intents. Six now resolve through an
// AskHelixMemoryArtifact contextKind and are registered below — the governed P&L
// trio plus the three F-INVEST scenario-investment artifacts. The remaining two do
// not, and registering them would invent a lookup the engine never performs:
//   - active_strategy_objective_binding — resolved from the active strategy's
//     objective targets via attachObjectiveTargets(), not the artifact store.
//   - approved_cost_ratio_formula_ref — a formula reference (finance.rules.ts),
//     governed by the approved-formula lifecycle, not by evidence artifacts.
// They stay CONTRACT_ABSENT under the evidence instrument on purpose.
//
// F-INVEST SUCCESSOR SUBSTRATE — SEQUENCING FULFILLED (#4602 Q1-FIX PR-B).
// PR-A (#4716) typed the substrate in
// packages/lib/src/scenario-investment-evidence-artifacts.ts and recorded, here, that
// registration was unlawful until the module itself declared the keys and the engine
// itself queried the literals. PR-B does exactly the three things that note named, in
// ONE change:
//   1. finance.manifest.ts now declares all three keys — on the
//      scenario-investment intent family and in requiredState -
//      satisfying "finance: every contract evidenceKey is declared by the module
//      itself";
//   2. FinanceGovernedDataLakeSource.attachScenarioInvestmentEvidence() now queries
//      exactly the contextKind/schemaVersion pairs registered below, so
//      "finance contextKind/schemaVersion match the engine's own query literals" is
//      satisfied per key (that test is now a per-key pin split, the pricing-realization
//      shape: the P&L trio keeps the engine pair, each F-INVEST key pins to its own
//      artifact literals);
//   3. the four retired pseudo-keys — investment_requirement_cost_basis,
//      capability_requirement_map, technology_investment_requirement_evidence,
//      roi_denominator_evidence — leave the manifest in the same PR
//      (docs/capabilities/homework/finance-evidence-design-20260710.md §5, "Replace
//      the current pseudo-keys"), which is what makes the registration census's
//      shrink-only per-key exemption ledger lawfully lose them. Their content is not
//      lost: capability, technology, cost-basis, and ROI-denominator completeness are
//      validated subfields and typed gaps INSIDE the three artifacts, which is where
//      the design puts them.
//
// The intent stays honest in both directions. With the artifacts absent, the rules
// layer still returns a named-gap block — naming the three TYPED keys instead of the
// four pseudo-keys. With them present, it returns a governed evaluation whose every
// number is computed from the parsed artifacts (requirement costUsd bindings, value-
// case risk-adjusted benefit, approved funding), and whose ROI and funding-gap metrics
// are WITHHELD as named gaps whenever a named-cost-gap row means the requirement total
// is incomplete — a named gap never contributes zero to a total or an ROI denominator.
export const FINANCE_EVIDENCE_KEYS = {
  pnlArtifact: 'approved_finance_pnl_artifact',
  chartOfAccountsMapping: 'approved_chart_of_accounts_mapping',
  ebitdaDefinition: 'approved_ebitda_definition',
} as const;

export const FINANCE_CONTEXT_KINDS = {
  governedPnlSnapshot: 'decision_agent:finance:governed_pnl_snapshot',
} as const;

export const FINANCE_SCHEMA_VERSIONS = {
  governedPnlSnapshot: 'finance_pnl_governed_snapshot_v1',
} as const;

const NO_FINANCE_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Every field below mirrors finance.manifest.ts requiredState for the same
// stateKey (sourceClass public-filing, domain finance, cadence ad_hoc,
// requiredFor monitoring/scenario/decision_pack/ask_helix, missingBehavior block).
const financeGovernedEntry = (
  evidenceKey: (typeof FINANCE_EVIDENCE_KEYS)[keyof typeof FINANCE_EVIDENCE_KEYS],
  description: string,
) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.publicFiling,
    contextKind: FINANCE_CONTEXT_KINDS.governedPnlSnapshot,
    schemaVersion: FINANCE_SCHEMA_VERSIONS.governedPnlSnapshot,
    bindingPlaceholders: NO_FINANCE_BINDING_PLACEHOLDERS,
    domain: 'finance',
    freshnessCadence: 'ad_hoc',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description,
  });

// The F-INVEST trio does NOT ride the governed P&L snapshot: each artifact has its
// own context kind and schema version (packages/lib/src/scenario-investment-evidence-
// artifacts.ts), so each entry pins its own pair rather than sharing one.
//
// sourceClass is `modeled` for all three, and that is the honest label, not a
// downgrade of the P&L trio: the value-case contract's value classes are closed to
// forecast/assumption (there is deliberately no 'observed' member), and a requirement
// may be costed off an industry_benchmark rung. Under Standing Law 15 a modeled
// artifact renders WITH the server-authored "Modeled" badge rather than being
// suppressed; the closed admissible set lives on the scenario-investment source system
// in approved-source-systems.ts, and the finance P&L source systems are untouched.
// Every field mirrors finance.manifest.ts requiredState for the same stateKey.
const financeScenarioInvestmentEntry = (
  evidenceKey: (typeof SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS)[keyof typeof SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS],
  contextKind: (typeof SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS)[keyof typeof SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS],
  schemaVersion: (typeof SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS)[keyof typeof SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS],
  description: string,
) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
    contextKind,
    schemaVersion,
    bindingPlaceholders: NO_FINANCE_BINDING_PLACEHOLDERS,
    domain: 'finance',
    freshnessCadence: 'ad_hoc',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description,
  });

export const FINANCE_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'finance',
  {
    pnl_artifact: financeGovernedEntry(
      FINANCE_EVIDENCE_KEYS.pnlArtifact,
      'Approved governed P&L snapshot published by the finance data-lake source; the artifact ' +
        'every finance read intent (operating income, EBITDA, margin, cost structure, run rate) ' +
        'resolves its figures from.',
    ),
    chart_of_accounts_mapping: financeGovernedEntry(
      FINANCE_EVIDENCE_KEYS.chartOfAccountsMapping,
      'Approved chart-of-accounts mapping carried by the same governed P&L snapshot; binds ' +
        'reported P&L lines to the governed account taxonomy the reconciliation intents cite.',
    ),
    ebitda_definition: financeGovernedEntry(
      FINANCE_EVIDENCE_KEYS.ebitdaDefinition,
      'Approved EBITDA definition carried by the same governed P&L snapshot; fixes which ' +
        'adjustments are in scope so EBITDA reads are reproducible rather than re-derived per run.',
    ),
    scenario_investment_requirements_artifact: financeScenarioInvestmentEntry(
      SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS.requirements,
      SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS.requirements,
      SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS.requirements,
      'Approved scenario investment-requirements artifact: canonical requirement rows ' +
        '(cost basis rung, named cost gaps, capability checks) plus the finance tie-out ' +
        'extension — accounting classification, GL/cost-pool/project mapping, cash-flow and ' +
        'P&L effect schedules. The governed denominator behind any what-it-takes or ROI read.',
    ),
    scenario_value_case_artifact: financeScenarioInvestmentEntry(
      SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS.valueCase,
      SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS.valueCase,
      SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS.valueCase,
      'Approved scenario value-case artifact at initiative x fiscal period x value metric ' +
        'grain: baseline, expected incremental value (forecast or assumption, never an ' +
        'observed actual), gross cash and P&L benefit, and the risk adjustment with its ' +
        'approved basis. The governed numerator behind any margin-growth ROI read.',
    ),
    scenario_funding_plan_artifact: financeScenarioInvestmentEntry(
      SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS.fundingPlan,
      SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS.fundingPlan,
      SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS.fundingPlan,
      'Approved scenario funding-plan artifact at funding source x fiscal period grain: ' +
        'approved amount, source kind, restriction, owner, approval ref, and Decision Ledger ' +
        'binding, with governed explicit-zero rows. Without it, requirements may be shown but ' +
        'funding-gap and affordability claims stay blocked.',
    ),
  },
  {
    evidenceKeys: [
      ...Object.values(FINANCE_EVIDENCE_KEYS),
      ...Object.values(SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS),
    ],
    sourceClasses: [
      DECISION_AGENT_SOURCE_CLASSES.publicFiling,
      DECISION_AGENT_SOURCE_CLASSES.modeled,
    ],
    contextKinds: [
      ...Object.values(FINANCE_CONTEXT_KINDS),
      ...Object.values(SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS),
    ],
    schemaVersions: [
      ...Object.values(FINANCE_SCHEMA_VERSIONS),
      ...Object.values(SCENARIO_INVESTMENT_ARTIFACT_SCHEMA_VERSIONS),
    ],
    stateKeys: [
      ...Object.values(FINANCE_EVIDENCE_KEYS),
      ...Object.values(SCENARIO_INVESTMENT_ARTIFACT_EVIDENCE_KEYS),
    ],
    bindingPlaceholders: NO_FINANCE_BINDING_PLACEHOLDERS,
  },
);

export type FinanceModuleEvidenceContract = typeof FINANCE_MODULE_EVIDENCE_CONTRACT;
